import _regeneratorRuntime from "/data/home/fjc/workspace/tfjs-yolov5-example/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _asyncToGenerator from "/data/home/fjc/workspace/tfjs-yolov5-example/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";

/**
 * @license
 * Copyright 2020 Google Inc. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { TensorBuffer } from '../tensor';
import { convertToTensor } from '../tensor_util_env';
import * as util from '../util';
/**
 * Computes the difference between two lists of numbers.
 *
 * Given a Tensor `x` and a Tensor `y`, this operation returns a Tensor `out`
 * that represents all values that are in `x` but not in `y`. The returned
 * Tensor `out` is sorted in the same order that the numbers appear in `x`
 * (duplicates are preserved). This operation also returns a Tensor indices that
 * represents the position of each out element in `x`. In other words:
 *
 * `out[i] = x[idx[i]] for i in [0, 1, ..., out.length - 1]`
 *
 * ```js
 * const x = [1, 2, 3, 4, 5, 6];
 * const y = [1, 3, 5];
 *
 * const [out, indices] = await tf.setdiff1dAsync(x, y);
 * out.print(); // [2, 4, 6]
 * indices.print(); // [1, 3, 5]
 * ```
 *
 * @param x 1-D Tensor. Values to keep.
 * @param y 1-D Tensor. Must have the same type as x. Values to exclude in the
 *     output.
 * @returns Promise of Tensor tuple [out, indices].
 *  out: Tensor with the same type as x.
 *  indices: A Tensor of type int32.
 *
 * @doc {heading: 'Tensors', subheading: 'Transformations'}
 */

function setdiff1dAsync_(_x, _x2) {
  return _setdiff1dAsync_.apply(this, arguments);
}

function _setdiff1dAsync_() {
  _setdiff1dAsync_ = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(x, y) {
    var $x, $y, xVals, yVals, ySet, outputSize, i, buffer, indices, _i, p;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            $x = convertToTensor(x, 'x', 'setdiff1d');
            $y = convertToTensor(y, 'y', 'setdiff1d');
            util.assert($x.dtype === $y.dtype, function () {
              return "x and y should have the same dtype, but got x (".concat($x.dtype, ") and y (").concat($y.dtype, ").");
            });
            util.assert($x.rank === 1, function () {
              return "x should be 1D tensor, but got x (".concat($x.shape, ").");
            });
            util.assert($y.rank === 1, function () {
              return "y should be 1D tensor, but got y (".concat($y.shape, ").");
            });
            _context.next = 7;
            return $x.data();

          case 7:
            xVals = _context.sent;
            _context.next = 10;
            return $y.data();

          case 10:
            yVals = _context.sent;
            ySet = new Set(yVals);
            outputSize = 0;

            for (i = 0; i < xVals.length; i++) {
              if (!ySet.has(xVals[i])) {
                outputSize++;
              }
            }

            buffer = new TensorBuffer([outputSize], $x.dtype);
            indices = new TensorBuffer([outputSize], 'int32');

            for (_i = 0, p = 0; _i < xVals.length; _i++) {
              if (!ySet.has(xVals[_i])) {
                buffer.values[p] = xVals[_i];
                indices.values[p] = _i;
                p++;
              }
            }

            return _context.abrupt("return", [buffer.toTensor(), indices.toTensor()]);

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _setdiff1dAsync_.apply(this, arguments);
}

export var setdiff1dAsync = setdiff1dAsync_;